<template>
  <div>
    <b-table
      responsive
      :busy="isBusy"
      striped
      hover
      show-empty
      empty-text="آیتمی جهت نمایش وجود ندارد"
      :items="items"
      :fields="fields"
      :tbody-tr-class="rowClass"
    >
      <template v-slot:table-busy>
        <div class="text-center my-4">
          <b-spinner class="align-middle"></b-spinner>
          <strong class="mr-2">در حال دریافت اطلاعات...</strong>
        </div>
      </template>
      <template #cell(transferId)="row">
        <copyable-table-cell
          :value="row.item.transferId"
          label="شناسه ترنسفر"
        />
      </template>
      <template #cell(orderId)="row">
        <copyable-table-cell :value="row.item.orderId" label="شناسه سفارش" />
      </template>
      <template #cell(destinationIban)="row">
        <copyable-table-cell
          :value="row.item.destinationIban"
          label="شماره شبا مقصد"
        />
      </template>
      <template #cell(status)="row">
        <b-badge :variant="getStatusVariant(row.item.status)" pill>
          {{ getStatusLabel(row.item.status) }}
        </b-badge>
      </template>
    </b-table>

    <b-pagination
      v-if="totalRows > pageSize"
      v-model="currentPage"
      class="mt-3"
      :total-rows="totalRows"
      :per-page="pageSize"
      @change="fetchItems"
    ></b-pagination>
  </div>
</template>

<script>
import httpHelper from "../helpers/httpHelper";
import CopyableTableCell from "./CopyableTableCell";
import moment from "jalali-moment";
import { EMPTY_VALUE } from "../helpers/copyableHelper";

const STATUS_MAP = {
  QUEUED: { label: "درصف بررسی", variant: "warning" },
  FAILED: { label: "خطا", variant: "danger" },
  SUCCESS: { label: "اقدام به ترنسفر", variant: "success" },
};

export default {
  name: "ExcelUploadBatchDetails",
  components: {
    CopyableTableCell,
  },
  props: {
    batchId: {
      type: String,
      required: true,
    },
    gatewayPin: {
      type: [String, Number],
      default: null,
    },
    bankOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      items: [],
      isBusy: false,
      currentPage: 1,
      pageSize: 50,
      totalRows: 0,
      fields: [
        {
          key: "orderId",
          label: "شناسه سفارش",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "paymentId",
          label: "شناسه واریز",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "transferId",
          label: "شناسه ترنسفر",
        },
        {
          key: "amount",
          label: "مبلغ (ریال)",
          formatter: (value) => this.formatNumber(value),
          tdClass: "ltr-dir",
        },
        {
          key: "destinationIban",
          label: "شماره شبا مقصد",
        },
        {
          key: "destinationFirstName",
          label: "نام",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "destinationLastName",
          label: "نام خانوادگی",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "destinationBank",
          label: "بانک مقصد",
          formatter: (value) => this.getBankLabel(value),
        },
        {
          key: "status",
          label: "وضعیت",
        },
        {
          key: "validationError",
          label: "خطای اعتبارسنجی",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "createdAt",
          label: "تاریخ ایجاد",
          formatter: (value) => this.formatDateTime(value),
          tdClass: "ltr-dir",
        },
        {
          key: "updatedAt",
          label: "تاریخ بروزرسانی",
          formatter: (value) => this.formatDateTime(value),
          tdClass: "ltr-dir",
        },
      ],
    };
  },
  watch: {
    batchId: {
      immediate: true,
      handler() {
        this.fetchItems(1);
      },
    },
  },
  methods: {
    rowClass(item, type) {
      if (!item || type !== "row") {
        return "";
      }
      return item.status === "FAILED" ? "excel-upload-row-failed" : "";
    },
    formatDateTime(value) {
      if (!value) {
        return EMPTY_VALUE;
      }

      const date = moment(value);
      return date.isValid() ? date.format("jYYYY/jMM/jDD | HH:mm:ss") : value;
    },
    formatNumber(value) {
      if (value == null || value === "") {
        return EMPTY_VALUE;
      }

      const numericValue = Number(value);
      return Number.isFinite(numericValue)
        ? numericValue.toLocaleString()
        : String(value);
    },
    getBankLabel(code) {
      if (!code) {
        return EMPTY_VALUE;
      }

      const bank = this.bankOptions.find((option) => option.value === code);
      return bank ? bank.text : "نامعلوم";
    },
    getStatusLabel(status) {
      return (
        (STATUS_MAP[status] && STATUS_MAP[status].label) ||
        status ||
        EMPTY_VALUE
      );
    },
    getStatusVariant(status) {
      return (STATUS_MAP[status] && STATUS_MAP[status].variant) || "secondary";
    },
    fetchItems(page = 1) {
      if (!this.batchId || !this.gatewayPin) {
        this.items = [];
        this.totalRows = 0;
        return;
      }

      httpHelper.setAuth();
      this.isBusy = true;
      this.currentPage = page;

      httpHelper
        .getData(
          `~transfer/transfer/uploads/${encodeURIComponent(this.batchId)}?api=${
            this.gatewayPin
          }&page=${page}&perPage=${this.pageSize}`,
        )
        .then((res) => {
          this.items = res && res.data ? res.data : [];
          const meta = res && res.meta ? res.meta : {};
          this.totalRows = meta.total || this.items.length;
          this.currentPage = meta.page || page;
          this.pageSize = meta.perPage || this.pageSize;
        })
        .catch(() => {
          this.items = [];
          this.totalRows = 0;
        })
        .then(() => {
          this.isBusy = false;
        });
    },
  },
};
</script>

<style scoped>
::v-deep .excel-upload-row-failed {
  background-color: #ffe5e5 !important;
}

::v-deep .excel-upload-row-failed:hover {
  background-color: #ffd6d6 !important;
}
</style>
